import {
  Button,
  mergeClasses,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  ChatRegular,
  ChevronRightRegular,
} from "@fluentui/react-icons";
import { useCallback, useRef, useState } from "react";
import { ATD, ImpactAnalysis } from "apps/decooda/DecoodaReviews";
import { useNavigate } from "react-router-dom";
import { useAnalyticsResultsStyles } from "./AnalyticsResults.styles";
import { IAnalyticsResultsProps } from "./AnalyticsResults.types";

const REVIEW_PREVIEW_MAX_CHARS = 200;

const RESULTS_SUB_TAB = {
  ATD: "Auto Topic Discovery",
  IMPACT: "Impact Analysis",
} as const;
type ResultsSubTab = (typeof RESULTS_SUB_TAB)[keyof typeof RESULTS_SUB_TAB];
const RESULTS_SUB_TABS = Object.values(RESULTS_SUB_TAB) as ResultsSubTab[];

export const AnalyticsResults = ({
  project,
  analyticsId,
  workspaceId,
}: IAnalyticsResultsProps): JSX.Element => {
  const styles = useAnalyticsResultsStyles();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState<ResultsSubTab>(
    RESULTS_SUB_TAB.ATD,
  );
  const [isAtdDownloading, setIsAtdDownloading] = useState(false);
  const [isImpactDownloading, setIsImpactDownloading] = useState(false);
  const subTabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleSubTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = RESULTS_SUB_TABS.indexOf(activeSubTab);
      let nextIndex: number | null = null;
      if (e.key === "ArrowRight")
        nextIndex = (currentIndex + 1) % RESULTS_SUB_TABS.length;
      else if (e.key === "ArrowLeft")
        nextIndex =
          (currentIndex - 1 + RESULTS_SUB_TABS.length) %
          RESULTS_SUB_TABS.length;
      if (nextIndex !== null) {
        e.preventDefault();
        setActiveSubTab(RESULTS_SUB_TABS[nextIndex]);
        subTabRefs.current[nextIndex]?.focus();
      }
    },
    [activeSubTab],
  );

  const isDownloading = isAtdDownloading || isImpactDownloading;

  const handleDownload = useCallback(() => {
    if (activeSubTab === RESULTS_SUB_TAB.ATD) {
      setIsAtdDownloading(true);
    } else {
      setIsImpactDownloading(true);
    }
  }, [activeSubTab]);

  const downloadTooltip = isDownloading
    ? "Export in progress"
    : activeSubTab === RESULTS_SUB_TAB.ATD
      ? "Export Topics Results"
      : "Export Impact Analysis Results";

  return (
    <div className={styles.root}>
      <div
        className={styles.subTabBar}
        role="tablist"
        aria-label="Results sections"
      >
        {RESULTS_SUB_TABS.map((tab, idx) => {
          const isActive = tab === activeSubTab;
          return (
            <button
              key={tab}
              ref={(el) => {
                subTabRefs.current[idx] = el;
              }}
              role="tab"
              aria-selected={isActive}
              aria-controls={`results-tabpanel-${tab}`}
              id={`results-tab-${tab}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveSubTab(tab)}
              onKeyDown={handleSubTabKeyDown}
              className={mergeClasses(
                styles.subTab,
                isActive && styles.subTabActive,
              )}
            >
              {tab}
            </button>
          );
        })}
      </div>

      <div
        id={`results-tabpanel-${activeSubTab}`}
        role="tabpanel"
        aria-labelledby={`results-tab-${activeSubTab}`}
      >
        {activeSubTab === RESULTS_SUB_TAB.ATD && (
          <ATD
            projectId={analyticsId}
            projectName={project.name}
            startDownload={isAtdDownloading}
            setStartDownload={setIsAtdDownloading}
            showCards={true}
            cardTitle={RESULTS_SUB_TAB.ATD}
            hideExtendedColumns={true}
            reviewMaxChars={REVIEW_PREVIEW_MAX_CHARS}
            cardHeaderAction={
              <Tooltip content={downloadTooltip} relationship="label">
                <Button
                  appearance="subtle"
                  icon={
                    isAtdDownloading ? (
                      <Spinner size="tiny" aria-hidden="true" />
                    ) : (
                      <ArrowDownloadRegular aria-hidden="true" />
                    )
                  }
                  onClick={handleDownload}
                  disabled={isDownloading}
                  aria-label={downloadTooltip}
                />
              </Tooltip>
            }
          />
        )}
        {activeSubTab === RESULTS_SUB_TAB.IMPACT && (
          <ImpactAnalysis
            projectId={analyticsId}
            projectName={project.name}
            startDownload={isImpactDownloading}
            setStartDownload={setIsImpactDownloading}
          />
        )}
      </div>
    </div>
  );
};
